import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import BreadCrumb from '../../components/breadCrumb';
import { getBeneficiaryById, getList } from '../../API/apiService';
import {
  formatDateInBogota,
  formatDateTimeInBogota
} from '../../utils/dateTime';
import {
  buildProtectedImageUrl,
  fetchWithAuth
} from '../../utils/secureMedia';
import { normalizeAdminScope } from '../../utils/adminScope';
import {getIdentificationTypeLabel} from '../../utils/identificationTypes';
import './style.scss';

const EMPTY_VALUE = 'No registra';

const NATIONALITY_LABELS = {
  CO: 'Colombiano(a)',
  VE: 'Venezolano(a)',
  OT: 'Otro'
};

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function isEmptyValue(value) {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && String(value).trim() === '')
  );
}

function displayValue(value) {
  if (isEmptyValue(value)) return EMPTY_VALUE;
  return String(value).trim();
}

function firstFilled(...values) {
  return values.find(value => !isEmptyValue(value));
}

function formatYesNo(value) {
  if (
    value === true ||
    value === 'Si' ||
    value === 'SI' ||
    value === '1' ||
    value === 1
  ) {
    return 'Si';
  }

  if (
    value === false ||
    value === 'No' ||
    value === 'NO' ||
    value === '0' ||
    value === 0
  ) {
    return 'No';
  }

  return displayValue(value);
}

function formatDate(value, includeTime = false) {
  if (!value) return EMPTY_VALUE;

  if (includeTime) {
    return formatDateTimeInBogota(value) || displayValue(value);
  }

  return formatDateInBogota(value) || displayValue(value);
}

function calculateAge(explicitAge, birthDate) {
  if (!isEmptyValue(explicitAge)) {
    return `${explicitAge} años`;
  }

  if (!birthDate) return EMPTY_VALUE;

  const birthday = new Date(birthDate);
  if (Number.isNaN(birthday.getTime())) return EMPTY_VALUE;

  const now = new Date();
  let age = now.getFullYear() - birthday.getFullYear();
  const month = now.getMonth() - birthday.getMonth();

  if (month < 0 || (month === 0 && now.getDate() < birthday.getDate())) {
    age -= 1;
  }

  return Number.isNaN(age) ? EMPTY_VALUE : `${age} años`;
}

function resolveGenderValue(gender, genders) {
  if (!Array.isArray(genders) || genders.length === 0) {
    return displayValue(gender);
  }

  const found = genders.find(option =>
    option?.id?.toString() === gender?.toString() ||
    option?.code?.toString() === gender?.toString() ||
    option?.value?.toString() === gender?.toString()
  );

  return displayValue(found?.value ?? gender);
}

function resolveNationality(value) {
  const normalized = String(value || '').trim();

  if (!normalized) {
    return {
      label: EMPTY_VALUE,
      other: EMPTY_VALUE
    };
  }

  if (NATIONALITY_LABELS[normalized]) {
    return {
      label: NATIONALITY_LABELS[normalized],
      other: normalized === 'OT' ? EMPTY_VALUE : EMPTY_VALUE
    };
  }

  return {
    label: 'Otro',
    other: normalized
  };
}

function resolveOtherField(mainValue, otherValue) {
  if (normalizeText(mainValue) !== 'otro') {
    return EMPTY_VALUE;
  }

  return displayValue(otherValue);
}

function formatValueWithOther(mainValue, otherValue) {
  const otherDetail = resolveOtherField(mainValue, otherValue);

  if (otherDetail === EMPTY_VALUE) {
    return displayValue(mainValue);
  }

  return `${displayValue(mainValue)}: ${otherDetail}`;
}

function formatNationalityValue(nationality) {
  if (!nationality || nationality.label === EMPTY_VALUE) {
    return EMPTY_VALUE;
  }

  if (nationality.other === EMPTY_VALUE) {
    return nationality.label;
  }

  return `${nationality.label}: ${nationality.other}`;
}

function formatDisplacement(time, place) {
  const details = [];

  if (!isEmptyValue(time)) {
    details.push(`Tiempo: ${displayValue(time)}`);
  }

  if (!isEmptyValue(place)) {
    details.push(`Lugar: ${displayValue(place)}`);
  }

  return details.length > 0 ? details.join(' | ') : EMPTY_VALUE;
}

function pushIfMissing(values, nextValue) {
  const normalizedNextValue = normalizeText(nextValue);
  if (!normalizedNextValue) return;

  const exists = values.some(value => normalizeText(value) === normalizedNextValue);
  if (!exists) values.push(nextValue);
}

function isAffirmative(value) {
  return normalizeText(formatYesNo(value)) === 'si';
}

function resolveCopDifferentialFocus(beneficiary) {
  const values = [];
  const ethnicValue = String(
    beneficiary?.ethnic_group_value ?? beneficiary?.ethnicGroupValue ?? ''
  ).trim();
  const riskValue = String(
    beneficiary?.risk_factor_value ?? beneficiary?.riskFactorValue ?? ''
  ).trim();
  const disabilityValue = String(
    beneficiary?.disability_value ??
      beneficiary?.disabilityValue ??
      beneficiary?.disability ??
      ''
  ).trim();

  if (ethnicValue && normalizeText(ethnicValue) !== 'ninguna') {
    ethnicValue.split(',').forEach(value => pushIfMissing(values, value.trim()));
  }
  if (isAffirmative(beneficiary?.lgbtiq)) {
    pushIfMissing(values, 'LGBTIQ+');
  }
  if (normalizeText(riskValue).includes('calle')) {
    pushIfMissing(values, 'Situacion de calle');
  }
  if (isAffirmative(beneficiary?.farmer)) {
    pushIfMissing(values, 'Campesino');
  }
  if (
    disabilityValue &&
    !normalizeText(disabilityValue).includes('no tiene') &&
    normalizeText(disabilityValue) !== 'ninguna'
  ) {
    pushIfMissing(values, 'Discapacidad');
  }

  return values.join(', ');
}

function resolveScopeFromReturnTo(returnTo) {
  const [path, search = ''] = String(returnTo || '').split('?');
  if (!path || !search) return '';
  return normalizeAdminScope(new URLSearchParams(search).get('scope'));
}

function normalizeFileNameSegment(value, fallback) {
  const normalized = String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return normalized || fallback;
}

function downloadBlob(blob, fileName) {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

function resolveDownloadFileName(contentDisposition, fallback) {
  const headerValue = String(contentDisposition || '');
  if (!headerValue) return fallback;

  const utf8Match = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const basicMatch = headerValue.match(/filename="?([^"]+)"?/i);
  if (basicMatch?.[1]) {
    return basicMatch[1];
  }

  return fallback;
}

function DetailField({ label, value, wide = false }) {
  return (
    <article className={`detail-field${wide ? ' detail-field--wide' : ''}`}>
      <p className='detail-field__label'>{label}</p>
      <p className='detail-field__value'>{displayValue(value)}</p>
    </article>
  );
}

function DetailSection({ title, description, fields }) {
  return (
    <section className='detail-card'>
      <div className='detail-card__header'>
        <h2 className='detail-card__title'>{title}</h2>
        {description ? (
          <p className='detail-card__description'>{description}</p>
        ) : null}
      </div>

      <div className='detail-card__body'>
        {fields.map(field => (
          <DetailField
            key={`${title}-${field.label}`}
            label={field.label}
            value={field.value}
            wide={field.wide}
          />
        ))}
      </div>
    </section>
  );
}

function BeneficiariesAdmin() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialBeneficiary = location.state ?? null;
  const [beneficiaryData, setBeneficiaryData] = useState(initialBeneficiary);
  const [genders, setGenders] = useState([]);
  const [isDownloadingConsent, setIsDownloadingConsent] = useState(false);
  const beneficiary = beneficiaryData ?? initialBeneficiary;
  const returnTo = searchParams.get('returnTo') || initialBeneficiary?.backTo || '';
  const adminScope =
    normalizeAdminScope(searchParams.get('scope')) ||
    resolveScopeFromReturnTo(returnTo);
  const isCopView = adminScope === 'cop';

  useEffect(() => {
    let cancelled = false;

    async function loadGenders() {
      const response = await getList('gender');
      if (!cancelled) {
        setGenders(Array.isArray(response) ? response : []);
      }
    }

    loadGenders();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadBeneficiaryDetail() {
      const beneficiaryId = Number(initialBeneficiary?.id);
      if (!Number.isInteger(beneficiaryId) || beneficiaryId <= 0) {
        return;
      }

      const response = await getBeneficiaryById(beneficiaryId);
      if (
        !cancelled &&
        response &&
        typeof response === 'object' &&
        !Array.isArray(response)
      ) {
        setBeneficiaryData(currentValue => ({
          ...(currentValue || {}),
          ...response
        }));
      }
    }

    loadBeneficiaryDetail();

    return () => {
      cancelled = true;
    };
  }, [initialBeneficiary]);

  if (!beneficiary) {
    return (
      <section className='beneficiariesTable-main-container'>
        <BreadCrumb />
        <div className='beneficiary-empty-state'>
          <h1 className='beneficiary-empty-state__title'>
            No pudimos cargar la caracterizacion
          </h1>
          <p className='beneficiary-empty-state__text'>
            Regresa al listado e intenta abrir nuevamente el beneficiario.
          </p>
        </div>
      </section>
    );
  }

  const beneficiaryDocumentType = firstFilled(
    beneficiary?.document_type,
    beneficiary?.documentType
  );
  const beneficiaryDocument = firstFilled(beneficiary?.document);
  const beneficiaryFullName = firstFilled(
    beneficiary?.full_name,
    beneficiary?.fullName
  );
  const beneficiaryBirthDate = firstFilled(
    beneficiary?.birth_date,
    beneficiary?.birthDate
  );
  const beneficiaryPhone = firstFilled(
    beneficiary?.cellphone,
    beneficiary?.cell_phone,
    beneficiary?.cellPhone
  );
  const beneficiaryStudyCurrently = firstFilled(
    beneficiary?.study_currently,
    beneficiary?.studyCurrently
  );
  const beneficiaryGradeOrStudy = firstFilled(
    beneficiary?.grade_or_study,
    beneficiary?.gradeOrStudy
  );
  const beneficiaryObtainedTitle = firstFilled(
    beneficiary?.obtained_title,
    beneficiary?.obtainedTitle
  );
  const beneficiaryMaritalStatus = firstFilled(
    beneficiary?.marital_status_value,
    beneficiary?.maritalStatusValue
  );
  const beneficiaryOccupation = firstFilled(
    beneficiary?.occupation_value,
    beneficiary?.occupationValue
  );
  const beneficiaryOtherVictim = firstFilled(
    beneficiary?.other_victim_value,
    beneficiary?.otherVictimValue
  );
  const beneficiaryVictimRegistry = firstFilled(
    beneficiary?.victim_registry,
    beneficiary?.victimRegistry
  );
  const beneficiaryDisplacementTime = firstFilled(
    beneficiary?.displacement_time,
    beneficiary?.displacementTime
  );
  const beneficiaryDisplacementPlace = firstFilled(
    beneficiary?.displacement_place,
    beneficiary?.displacementPlace
  );
  const beneficiaryNnaRightsProcess = firstFilled(
    beneficiary?.nna_rights_process,
    beneficiary?.nnaRightsProcess
  );
  const beneficiaryDisabilityCertificate = firstFilled(
    beneficiary?.disability_certificate,
    beneficiary?.disabilityCertificate
  );
  const beneficiaryChronicDisability = firstFilled(
    beneficiary?.chronic_disability_value,
    beneficiary?.chronicDisabilityValue
  );
  const beneficiaryObservations = firstFilled(beneficiary?.observations);
  const beneficiaryEmail = firstFilled(
    beneficiary?.email,
    beneficiary?.personalEmail
  );
  const institutionName = firstFilled(
    beneficiary?.school?.name,
    beneficiary?.school?.address,
    beneficiary?.school?.code
  );
  const beneficiaryParentPhone = firstFilled(
    beneficiary?.parent_phone,
    beneficiary?.parentPhone,
    beneficiary?.attendantPhone
  );
  const beneficiaryParentEmail = firstFilled(
    beneficiary?.parent_email,
    beneficiary?.parentEmail,
    beneficiary?.attendantEmail
  );

  const idTypeLabel = displayValue(
    getIdentificationTypeLabel(beneficiaryDocumentType)
  );
  const genderValue = resolveGenderValue(beneficiary?.gender, genders);
  const nationality = resolveNationality(beneficiary?.nationality);
  const sexualOrientationValue = formatValueWithOther(
    beneficiary?.sexual_orientation_value || beneficiary?.sexual_orientation,
    beneficiary?.sexual_orientation
  );
  const ethnicGroupLabel = displayValue(
    beneficiary?.ethnic_group_value || beneficiary?.ethnic_group
  );
  const victimPopulationValue = formatValueWithOther(
    beneficiary?.victim_population_value || beneficiary?.victim_population,
    beneficiaryOtherVictim
  );
  const disabilityValue = formatValueWithOther(
    beneficiary?.disability_value || beneficiary?.disability,
    beneficiary?.disability
  );
  const riskFactorValue = formatValueWithOther(
    beneficiary?.risk_factor_value || beneficiary?.risk_factor,
    beneficiary?.risk_factor
  );
  const ageValue = calculateAge(beneficiary?.age, beneficiaryBirthDate);
  const headerName = displayValue(beneficiaryFullName);
  const schoolCode = displayValue(beneficiary?.school?.code);
  const groupCode = displayValue(beneficiary?.group?.code);
  const displacementValue = formatDisplacement(
    beneficiaryDisplacementTime,
    beneficiaryDisplacementPlace
  );
  const consentImageId = Number(beneficiary?.consentImage?.id);
  const hasConsent = Number.isInteger(consentImageId) && consentImageId > 0;

  async function handleDownloadConsent() {
    if (!hasConsent || isDownloadingConsent) return;

    setIsDownloadingConsent(true);

    try {
      const response = await fetchWithAuth(
        buildProtectedImageUrl(consentImageId, false, Date.now()),
        {
          method: 'GET'
        }
      );

      if (!response.ok) {
        throw new Error('CONSENT_DOWNLOAD_FAILED');
      }

      const fileBlob = await response.blob();
      const fallbackFileName = `consentimiento_${normalizeFileNameSegment(
        beneficiaryDocument,
        'sin_documento'
      )}_${normalizeFileNameSegment(headerName, 'beneficiario')}.pdf`;
      const fileName = resolveDownloadFileName(
        response.headers.get('content-disposition'),
        fallbackFileName
      );

      downloadBlob(fileBlob, fileName);
      toast('Descarga de consentimiento iniciada', {
        type: 'success'
      });
    } catch (error) {
      console.error(error);
      toast('No se pudo descargar el consentimiento', {
        type: 'error'
      });
    } finally {
      setIsDownloadingConsent(false);
    }
  }

  const copSections = [
    {
      title: 'CARACTERIZACION COP',
      fields: [
        { label: 'Tipo de documento', value: idTypeLabel },
        {
          label: 'Numero de documento',
          value: beneficiaryDocument
        },
        { label: 'Nombres y apellidos completos', value: beneficiaryFullName },
        {
          label: 'Empresa/Entidad/Dependencia',
          value: beneficiary?.address,
          wide: true
        },
        {
          label: 'Cargo/Empleo/Calidad en la que actua',
          value: beneficiaryOccupation,
          wide: true
        },
        { label: 'Sexo (Hombre/mujer)', value: genderValue },
        { label: 'Rango edad', value: beneficiaryGradeOrStudy },
        {
          label: 'Enfoque diferencial',
          value: resolveCopDifferentialFocus(beneficiary),
          wide: true
        },
        {
          label: 'Poblacion victima',
          value: formatYesNo(
            beneficiary?.victim_population_value ?? beneficiary?.victim_population
          )
        },
        {
          label: 'Comuna en la que reside',
          value: beneficiary?.municipality?.name
        },
        { label: 'Correo electronico', value: beneficiaryEmail },
        { label: 'Organizacion', value: beneficiaryObservations },
        { label: 'Numero de contacto', value: beneficiaryPhone }
      ]
    }
  ];

  const defaultSections = [
    {
      title: 'INFORMACIÓN GENERAL DEL PARTICIPANTE:',
      fields: [
        { label: 'Nombres y apellidos completos', value: beneficiaryFullName },
        { label: 'Teléfono de contacto', value: beneficiaryPhone },
        { label: 'Correo electrónico', value: beneficiaryEmail },
        { label: 'Dirección de residencia', value: beneficiary?.address, wide: true },
        { label: 'Barrio/Vereda', value: beneficiary?.neighborhood?.name },
        { label: 'Comuna/Cgto', value: beneficiary?.municipality?.name },
        { label: 'Zona', value: beneficiary?.zone?.name },
        { label: 'Tipo Identificación', value: idTypeLabel },
        {
          label: 'Número de documento de identificación',
          value: beneficiaryDocument
        },
        { label: 'Edad', value: ageValue },
        { label: 'Fecha Nacimiento', value: formatDate(beneficiaryBirthDate) },
        { label: 'Sexo al nacer', value: genderValue },
        { label: 'Nacionalidad', value: formatNationalityValue(nationality) },
        { label: 'Estado Civil', value: beneficiaryMaritalStatus }
      ]
    },
    {
      title: 'INFORMACIÓN DE OCUPACIÓN',
      fields: [
        {
          label: 'Estudia actualmente',
          value: formatYesNo(beneficiaryStudyCurrently)
        },
        { label: 'Nombre Entidad Aliada', value: institutionName },
        { label: 'Grado o estudio que cursa', value: beneficiaryGradeOrStudy },
        { label: 'Nivel de escolaridad', value: beneficiary?.scholarship_value },
        { label: 'Título obtenido', value: beneficiaryObtainedTitle },
        { label: 'Trabaja actualmente', value: beneficiaryOccupation }
      ]
    },
    {
      title: 'ENFOQUES DIFERENCIALES',
      fields: [
        {
          label: 'Pertenece a la comunidad LGBTIQ+',
          value: formatYesNo(beneficiary?.lgbtiq)
        },
        { label: 'Orientación sexual', value: sexualOrientationValue },
        { label: 'Etnia', value: ethnicGroupLabel },
        {
          label: 'Población víctima',
          value: victimPopulationValue
        },
        {
          label: '¿Cuenta con inscripción en el registro único de víctimas?',
          value: formatYesNo(beneficiaryVictimRegistry)
        },
        {
          label: 'Tiempo y lugar de desplazamiento (en caso que aplique)',
          value: displacementValue,
          wide: true
        },
        {
          label: '¿Se identifica como campesino?',
          value: formatYesNo(beneficiary?.farmer)
        },
        {
          label: 'NNA en proceso administrativo de restablecimiento de derechos',
          value: formatYesNo(beneficiaryNnaRightsProcess)
        },
        { label: 'Enfoque de discapacidad', value: disabilityValue },
        {
          label: '¿Tiene certificado de discapacidad?',
          value: formatYesNo(beneficiaryDisabilityCertificate)
        },
        {
          label: '¿Tiene alguna enfermedad crónica?',
          value: formatYesNo(beneficiaryChronicDisability)
        }
      ]
    },
    {
      title: 'PROBLEMÁTICAS Y FACTORES DE RIESGO',
      fields: [
        {
          label: 'Problemáticas y factores de riesgo',
          value: riskFactorValue,
          wide: true
        },
        { label: 'Observaciones', value: beneficiaryObservations, wide: true }
      ]
    },
    {
      title: 'DATOS DEL ACUDIENTE',
      fields: [
        { label: 'Nombre completo', value: beneficiary?.parent },
        { label: 'Correo electrónico', value: beneficiaryParentEmail },
        { label: 'Teléfonos de contacto', value: beneficiaryParentPhone }
      ]
    }
  ];

  const sections = isCopView ? copSections : defaultSections;
  const heroChips = isCopView
    ? [
        { label: 'Empresa/Entidad', value: beneficiary?.address },
        { label: 'Organizacion', value: beneficiaryObservations },
        { label: 'Comuna', value: beneficiary?.municipality?.name }
      ]
    : [
        { label: 'Proceso Formativo', value: schoolCode },
        { label: 'Grupo', value: groupCode },
        { label: 'Comuna', value: beneficiary?.municipality?.name }
      ];

  return (
    <section className='beneficiariesTable-main-container'>
      <BreadCrumb />

      <section className='beneficiary-hero'>
        <div className='beneficiary-hero__copy'>
          <p className='beneficiary-hero__eyebrow'>
            {isCopView ? 'Caracterizacion COP' : 'Caracterizacion'}
          </p>
          <h1 className='beneficiary-hero__title'>{headerName}</h1>
          <p className='beneficiary-hero__subtitle'>
            {idTypeLabel} - {displayValue(beneficiaryDocument)}
          </p>
        </div>

        <div className='beneficiary-hero__aside'>
          <div className='beneficiary-hero__meta'>
            {heroChips.map(chip => (
              <div className='beneficiary-chip' key={chip.label}>
                <span className='beneficiary-chip__label'>{chip.label}</span>
                <span className='beneficiary-chip__value'>
                  {displayValue(chip.value)}
                </span>
              </div>
            ))}
          </div>

          {!isCopView && (
            <button
              type='button'
              className='beneficiary-hero__action'
              onClick={handleDownloadConsent}
              disabled={!hasConsent || isDownloadingConsent}>
              {isDownloadingConsent
                ? 'Descargando consentimiento...'
                : hasConsent
                  ? 'Descargar consentimiento'
                  : 'Sin consentimiento cargado'}
            </button>
          )}
        </div>
      </section>

      <section className='beneficiary-details'>
        {sections.map(section => (
          <DetailSection
            key={section.title}
            title={section.title}
            description={section.description}
            fields={section.fields}
          />
        ))}
      </section>
    </section>
  );
}

export default BeneficiariesAdmin;
